/** The ID of the filter for the projects without a tag */
export const UNTAGGED_FILTER_ID = "untagged";

/** Starts the tags the system gives the projects, e.g. "sys:demo", which the user cannot filter by */
const SYSTEM_TAG_PREFIX = "sys:";

/** Starts the ID of a filter for a tag, e.g. "tag:Bridge", so no tag is mistaken for the untagged filter */
const TAG_FILTER_ID_PREFIX = "tag:";

/** Returns the filter option for the projects without a tag */
export function getProjectsTagsFilterPresets() {
  return {
    [UNTAGGED_FILTER_ID]: {
      id: UNTAGGED_FILTER_ID,
      label: "Untagged",
      hasEntityPassedFilter: project => getProjectUserTags(project).length === 0
    }
  };
}

/**
 * Returns the filter options for the tags among the IDs, which match the projects with them. The
 * IDs that are no tag, or a system tag, are left out.
 */
export function getTagFilterOptions(filterIds) {
  return Object.fromEntries(filterIds.flatMap(filterId => {
    const tag = parseTagFilterId(filterId);
    if (tag === undefined) {
      return [];
    }
    return [[filterId, getTagFilterOption(tag)]];
  }));
}

/** Returns the filter option for the tag, which matches the projects with it */
export function getTagFilterOption(tag) {
  return {
    id: getTagFilterId(tag),
    label: tag,
    hasEntityPassedFilter: project => getProjectUserTags(project).includes(tag)
  };
}

/** Returns the ID of the filter for the tag */
export function getTagFilterId(tag) {
  return `${TAG_FILTER_ID_PREFIX}${tag}`;
}

/**
 * Returns the tag of the ID, or undefined if it is none, has no name or is a system tag. A name with
 * spaces around it is none either, as the tags of the projects are trimmed, so its ID would differ
 * from the one of the tag the dropdown shows, which could then not remove it.
 */
export function parseTagFilterId(filterId) {
  if (!filterId.startsWith(TAG_FILTER_ID_PREFIX)) {
    return undefined;
  }
  const tag = filterId.slice(TAG_FILTER_ID_PREFIX.length);
  return isUserTag(tag) && tag === tag.trim() ? tag : undefined;
}

/** Returns the tags of the projects the user can filter by, each once and sorted by name */
export function getProjectsTags(projects) {
  const tags = new Set(projects.flatMap(getProjectUserTags));
  return [...tags].sort((a, b) => a.localeCompare(b));
}

/** Returns the tags of the project the user gave it, without the system tags */
function getProjectUserTags(project) {
  return project.tags.map(tag => tag.trim()).filter(isUserTag);
}

/** Whether the tag has a name and is no system tag */
function isUserTag(tag) {
  return tag !== "" && !tag.startsWith(SYSTEM_TAG_PREFIX);
}